import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { pushApi } from "../../../api/push.api";
import { queryKeys } from "../../../lib/queryKeys";
import {
  PushSetupError,
  createPushSubscription,
  getPushSubscription,
  isPushSupported,
  notificationPermission,
  toSubscriptionInput,
} from "../webPush";

/**
 * unsupported: the browser has no Web Push. unavailable: the server has push turned off.
 * denied: the user blocked notifications for the site. enabled / disabled: for this browser.
 */
export type PushStatus =
  "loading" | "unsupported" | "unavailable" | "denied" | "enabled" | "disabled";

function usePushConfig() {
  return useQuery({
    queryKey: queryKeys.pushConfig,
    queryFn: ({ signal }) => pushApi.publicKey(signal),
    enabled: isPushSupported(),
    staleTime: Infinity,
  });
}

/** Push notifications on this device: the current state and the actions to change it. */
export function usePushNotifications() {
  const queryClient = useQueryClient();
  const supported = isPushSupported();
  const config = usePushConfig();
  const subscription = useQuery({
    queryKey: queryKeys.pushSubscription,
    queryFn: async () => (await getPushSubscription())?.endpoint ?? null,
    enabled: supported,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.pushSubscription });

  const enable = useMutation({
    mutationFn: async (permission: Promise<NotificationPermission>) => {
      if ((await permission) !== "granted") {
        throw new PushSetupError(
          "Notifications are blocked. Allow them in your browser's site settings and try again.",
        );
      }
      const publicKey = config.data;
      if (!publicKey) throw new PushSetupError("Push notifications are not set up on this server.");
      const created = await createPushSubscription(publicKey);
      await pushApi.subscribe(toSubscriptionInput(created));
    },
    onSettled: refresh,
  });

  const disable = useMutation({
    mutationFn: async () => {
      const current = await getPushSubscription();
      if (!current) return;
      await pushApi.unsubscribe(current.endpoint);
      await current.unsubscribe();
    },
    onSettled: refresh,
  });

  const sendTest = useMutation({ mutationFn: () => pushApi.sendTest() });

  const permission = notificationPermission();
  let status: PushStatus;
  if (!supported) status = "unsupported";
  else if (config.isPending || subscription.isPending) status = "loading";
  else if (config.data === null) status = "unavailable";
  else if (permission === "denied") status = "denied";
  else if (subscription.data && permission === "granted") status = "enabled";
  else status = "disabled";

  return {
    status,
    error: config.error ?? subscription.error,
    retry: () => {
      void config.refetch();
      void subscription.refetch();
    },
    /**
     * Call from a click handler: browsers only show the permission prompt during a user gesture,
     * so it is requested here, before any other await.
     */
    enable: () => {
      enable.mutate(Notification.requestPermission());
    },
    enabling: enable.isPending,
    enableError: enable.error,
    disable: disable.mutate,
    disabling: disable.isPending,
    disableError: disable.error,
    sendTest: sendTest.mutateAsync,
    sendingTest: sendTest.isPending,
  };
}

/**
 * Re-registers this browser's subscription with the server once per app load. It covers a
 * browser that renewed its subscription, a server whose keys were rotated, and a server that
 * lost track of the device.
 */
export function usePushSync(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isPushSupported() || Notification.permission !== "granted") return;
    // Idempotent, so a repeated run (Strict Mode, remount) does no harm and needs no cancelling.
    void (async () => {
      try {
        if (!(await getPushSubscription())) return;
        const publicKey = await queryClient.query({
          queryKey: queryKeys.pushConfig,
          queryFn: ({ signal }) => pushApi.publicKey(signal),
          staleTime: Infinity,
        });
        if (!publicKey) return;
        await pushApi.subscribe(toSubscriptionInput(await createPushSubscription(publicKey)));
      } catch {
        // Not worth interrupting anyone: settings show the state and can turn push on again.
      } finally {
        void queryClient.invalidateQueries({ queryKey: queryKeys.pushSubscription });
      }
    })();
  }, [queryClient]);
}

/** The service worker asks an open tab to show a clicked notification's page. */
export function useNotificationClicks(navigate: (url: string) => void): void {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const container = navigator.serviceWorker;
    const onMessage = (event: MessageEvent<unknown>) => {
      const data = event.data;
      if (
        typeof data === "object" &&
        data !== null &&
        "type" in data &&
        data.type === "OPEN_URL" &&
        "url" in data &&
        typeof data.url === "string" &&
        data.url.startsWith("/") &&
        !data.url.startsWith("//")
      ) {
        navigate(data.url);
      }
    };
    container.addEventListener("message", onMessage);
    container.startMessages();
    return () => {
      container.removeEventListener("message", onMessage);
    };
  }, [navigate]);
}
